// @mui material components

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { CREATE_NEW_COMPANY, ADMIN_UPDATE_COMPANY } from "lib/constants";
import { Autocomplete, FormHelperText, TextField } from "@mui/material";
import MDInput from "components/MDInput";
import { displayDate, FALLBACK_DATE_FORMAT } from "utils/Date";
import MDDatePicker from "components/MDDatePicker";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import useYupValidationResolver from "utils/yup/useYupValidationResolver";
import { postAPICall, putAPICall } from "lib/callApi";
import momentTz from "moment-timezone";

function CreateNewCompanyForm({
  isLoading,
  setIsLoading,
  handleModalClose,
  editCompanyDetailData,
  planTypeOptions,
  industriesList,
  companyRolesList,
}) {
  const navigate = useNavigate();
  const validSalesBricksIdExistsInEditMode =
    !!editCompanyDetailData?.subscriptionDetails?.isValidSubscriptionId;

  const [initialValues] = useState({
    companyName: editCompanyDetailData?.name || "",
    name: editCompanyDetailData?.Users?.[0]?.name || "",
    email: editCompanyDetailData?.email || "",
    planType: planTypeOptions?.find((planType) =>
      editCompanyDetailData?.subscriptionDetails?.planName
        ?.toLowerCase()
        ?.includes(planType?.title?.toLowerCase())
    )?.id,
    seatCount: editCompanyDetailData?.subscriptionDetails?.seatCount || null,
    salesBricksId: editCompanyDetailData?.externalSubscriptionId || "",
    subscriptionEndDate: editCompanyDetailData?.subscriptionDetails?.endDate
      ? moment(editCompanyDetailData.subscriptionDetails.endDate).toDate()
      : moment().add(2, "week").toDate(),
    industry: industriesList?.find(
      (industry) => editCompanyDetailData?.FK_company_industries_id === industry?.value
    )?.value,
    adminCompanyRole: companyRolesList?.find(
      (role) =>
        editCompanyDetailData?.UserCompaniesLinks?.[0]?.UserCompanyRole?.role_name ===
          role?.role_name || []
    )?.role_name,
  });

  const validationSchema = Yup.object({
    companyName: Yup.string().required("Company name is required"),
    name: Yup.string().required("Name is required"),
    email: Yup.string().required("Email is required"),
    planType: Yup.number().nullable().required("Plan Type is required"),
    adminCompanyRole: Yup[editCompanyDetailData ? "string" : "number"]()
      .nullable()
      .required("Company Role is required"),
    seatCount: Yup.number()
      .nullable()
      .positive("Seat Count must be a greater than zero")
      .required("Seat Count is required"),
    salesBricksId: Yup.string().nullable(),
    subscriptionEndDate: Yup.string().nullable(),
    industry: Yup.number().nullable().required("Industry is required"),
  });

  const resolver = useYupValidationResolver(validationSchema);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ resolver, reValidateMode: "all", mode: "all", defaultValues: initialValues });

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const result = editCompanyDetailData
        ? await putAPICall(`${ADMIN_UPDATE_COMPANY}/${editCompanyDetailData.id}`, true, data)
        : await postAPICall(CREATE_NEW_COMPANY, true, { ...data, timezone: momentTz.tz.guess() });
      if (result.data.success) {
        setIsLoading(false);
        reset();
        navigate("/registrations");
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
        handleModalClose?.(true);
      } else {
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <MDBox
        sx={{
          "& .MuiFormHelperText-root": { color: "#d32f2f" },
        }}
      >
        <MDBox mb={2}>
          <MDInput
            label="Company Name"
            variant="standard"
            fullWidth
            error={errors?.companyName?.message}
            disabled={isLoading}
            {...register("companyName")}
          />
          {errors?.companyName?.message ? (
            <FormHelperText>{errors.companyName.message}</FormHelperText>
          ) : null}
        </MDBox>
        <MDBox mb={2}>
          <MDInput
            label="Name"
            variant="standard"
            fullWidth
            error={errors?.name?.message}
            disabled={isLoading}
            {...register("name")}
          />
          {errors?.name?.message ? <FormHelperText>{errors.name.message}</FormHelperText> : null}
        </MDBox>
        <MDBox mb={2}>
          <MDInput
            label="Email"
            variant="standard"
            fullWidth
            error={errors?.email?.message}
            disabled={isLoading || !!editCompanyDetailData}
            {...register("email")}
          />
          {errors?.email?.message ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
        </MDBox>

        <MDBox mb={2}>
          {editCompanyDetailData ? (
            <MDInput
              label="Admin User Role"
              variant="standard"
              fullWidth
              disabled
              {...register("adminCompanyRole")}
            />
          ) : (
            <Controller
              control={control}
              name="adminCompanyRole"
              rules={{ required: true }}
              disabled={isLoading}
              render={({ field: { value } }) => (
                <Autocomplete
                  autoComplete
                  includeInputInList
                  id="adminCompanyRole"
                  fullWidth
                  options={companyRolesList}
                  value={(value && companyRolesList.find((option) => option.id === value)) || null}
                  getOptionLabel={(option) => option?.role_name || ""}
                  isOptionEqualToValue={(option, val) => option?.id === val?.id}
                  onChange={(_, option) => setValue("adminCompanyRole", option?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Admin User Role"
                      variant="standard"
                      helperText={errors?.adminCompanyRole?.message}
                    />
                  )}
                />
              )}
            />
          )}
          {errors?.adminCompanyRole?.message ? (
            <FormHelperText>{errors.adminCompanyRole.message}</FormHelperText>
          ) : null}
        </MDBox>
        <MDBox mb={2}>
          <Controller
            control={control}
            name="industry"
            rules={{ required: true }}
            disabled={isLoading}
            render={({ field: { value } }) => (
              <Autocomplete
                autoComplete
                includeInputInList
                id="industry"
                fullWidth
                options={industriesList}
                value={(value && industriesList.find((option) => option.value === value)) || null}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, val) => option?.value === val?.value}
                onChange={(_, option) => setValue("industry", option?.value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Industry"
                    variant="standard"
                    error={errors?.industry?.message}
                    helperText={errors?.industry?.message}
                  />
                )}
              />
            )}
          />
        </MDBox>
        <MDBox mb={2}>
          <Controller
            control={control}
            name="planType"
            rules={{ required: true }}
            disabled={isLoading}
            render={({ field: { value } }) => (
              <Autocomplete
                autoComplete
                includeInputInList
                id="planType"
                fullWidth
                value={(value && planTypeOptions.find((option) => option.id === value)) || null}
                onChange={(_, option) => setValue("planType", option?.id)}
                options={planTypeOptions}
                getOptionLabel={(option) => option?.title || ""}
                isOptionEqualToValue={(option, val) => option?.id === val?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Plan Type"
                    error={errors?.planType?.message}
                    helperText={errors?.planType?.message}
                  />
                )}
                disabled={isLoading || validSalesBricksIdExistsInEditMode}
              />
            )}
          />
        </MDBox>
        <MDBox mb={2}>
          <MDInput
            type="number"
            label="Seat Count"
            variant="standard"
            fullWidth
            error={errors?.seatCount?.message}
            {...register("seatCount")}
            disabled={isLoading || validSalesBricksIdExistsInEditMode}
          />
          {errors?.seatCount?.message ? (
            <FormHelperText>{errors.seatCount.message}</FormHelperText>
          ) : null}
        </MDBox>
        <MDBox mb={2}>
          <MDInput
            label="*Optional* Salesbricks ID"
            variant="standard"
            fullWidth
            error={errors?.salesBricksId?.message}
            {...register("salesBricksId")}
            disabled={isLoading || validSalesBricksIdExistsInEditMode}
          />
          {errors?.salesBricksId?.message ? (
            <FormHelperText>{errors.salesBricksId.message}</FormHelperText>
          ) : null}
        </MDBox>
        <MDBox mb={2}>
          <Controller
            control={control}
            name="subscriptionEndDate"
            rules={{ required: true }}
            render={({ field: { value } }) => (
              <MDDatePicker
                input={{
                  placeholder: "Select a date",
                  label: "*Optional* Subscription end date",
                  value: displayDate(value),
                  fullWidth: true,
                  variant: "standard",
                  disabled: isLoading || validSalesBricksIdExistsInEditMode,
                }}
                value={value}
                options={{
                  minDate: moment().add(1, "day").toDate(),
                  parseDate: (dateString) => moment(dateString, FALLBACK_DATE_FORMAT).toDate(),
                  formatDate: (date) => displayDate(date),
                  disableMobile: "true",
                }}
                onChange={(e) => setValue("subscriptionEndDate", moment(e[0]).endOf("D").toDate())}
              />
            )}
          />
          {errors?.subscriptionEndDate?.message ? (
            <FormHelperText>{errors.subscriptionEndDate.message}</FormHelperText>
          ) : null}
        </MDBox>
        <MDBox mt={4} mb={1}>
          <MDButton disabled={isLoading} color="primaryRed" fullWidth type="submit">
            {editCompanyDetailData ? "UPDATE COMPANY" : "CREATE COMPANY"}
          </MDButton>
        </MDBox>
      </MDBox>
    </form>
  );
}

export default CreateNewCompanyForm;
